/**
 * Enkel talaktivitetsdetektor (VAD) för mikrofonknappen, matad med
 * ljudnivåer (RMS 0-1) från en AnalyserNode några gånger per sekund.
 *
 * - Brusgolvet kalibreras på de första `calibrateMs`; tal = nivå tydligt över
 *   golvet (och över ett absolut minimum, så att en helt tyst mikrofon inte
 *   räknar sitt eget brus som tal).
 * - `speech-start` när det varit tal i sammanlagt `minSpeechMs`.
 * - `end-of-speech` när det efter tal varit tyst i `silenceMs`. Naturliga
 *   pauser mitt i en mening är 0,6-0,9 s, så 1,5 s klipper inte den som tänker.
 * - `no-speech` om inget tal kommit inom `noSpeechMs`.
 *
 * Varje händelse rapporteras en gång. Ren logik utan DOM, så den kan testas.
 */

export type VoiceActivityEvent = 'speech-start' | 'end-of-speech' | 'no-speech'

export const VAD_DEFAULTS = {
	calibrateMs: 300,
	minSpeechMs: 150,
	silenceMs: 1500,
	noSpeechMs: 5000,
	absoluteMin: 0.012,
	floorFactor: 3,
}

export function createVoiceActivity(opts: Partial<typeof VAD_DEFAULTS> = {}) {
	const o = { ...VAD_DEFAULTS, ...opts }
	let startedAt: number | null = null
	let lastT: number | null = null
	let floor = Infinity
	let speechMs = 0
	let speaking = false
	let lastVoiceAt = 0
	let ended = false
	let gaveUp = false

	return {
		get speechDetected() {
			return speaking
		},
		push(rms: number, t: number): VoiceActivityEvent | null {
			if (startedAt === null) startedAt = t
			const dt = lastT === null ? 0 : t - lastT
			lastT = t
			const elapsed = t - startedAt
			if (elapsed < o.calibrateMs) {
				floor = Math.min(floor, rms)
				return null
			}
			const threshold = Math.max(
				o.absoluteMin,
				(Number.isFinite(floor) ? floor : 0) * o.floorFactor,
			)
			if (rms > threshold) {
				speechMs += dt
				lastVoiceAt = t
				if (!speaking && speechMs >= o.minSpeechMs) {
					speaking = true
					return 'speech-start'
				}
				return null
			}
			if (speaking && !ended && t - lastVoiceAt >= o.silenceMs) {
				ended = true
				return 'end-of-speech'
			}
			if (!speaking && !gaveUp && elapsed >= o.noSpeechMs) {
				gaveUp = true
				return 'no-speech'
			}
			return null
		},
	}
}
